/**
 * Thin LiveOps schedule for EC2_EVENTS. Presentation/deals live in Unity
 * Addressable catalogs; the server only authors schedule + contentId + customJSON.
 */

export interface LiveOpsScheduleRow {
  id: string;
  contentId: string;
  active: boolean;
  startDate: string;
  endDate: string;
  /** Opaque object for the Addressable prefab/SO; never deep-validated. */
  customJSON: Record<string, unknown> | null;
}

export interface EventsConfig {
  lobbyBanners: LiveOpsScheduleRow[];
  eventBanners: LiveOpsScheduleRow[];
  events: LiveOpsScheduleRow[];
}

export type LiveOpsListKey = keyof EventsConfig;

export interface EventIssue {
  severity: "error" | "warning";
  path: string;
  message: string;
}

const ROOT_KEYS = ["lobbyBanners", "eventBanners", "events"] as const;
const ROW_KEYS = ["id", "contentId", "active", "startDate", "endDate", "customJSON"] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isDateString(value: unknown): value is string {
  return typeof value === "string" && !Number.isNaN(new Date(value).getTime());
}

function addUnknownKeyIssues(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
  path: string,
  issues: EventIssue[]
): void {
  const allowed = new Set(allowedKeys);
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      issues.push({
        severity: "error",
        path: `${path}.${key}`,
        message: `Unknown key "${key}" — the game will silently ignore it.`,
      });
    }
  }
}

function validateScheduleRow(value: unknown, path: string, seenIds: Set<string>, issues: EventIssue[]): void {
  if (!isPlainObject(value)) {
    issues.push({ severity: "error", path, message: "Schedule row must be an object." });
    return;
  }

  addUnknownKeyIssues(value, ROW_KEYS, path, issues);

  if (!isNonEmptyString(value.id)) {
    issues.push({ severity: "error", path: `${path}.id`, message: "id must be a non-empty string." });
  } else if (seenIds.has(value.id)) {
    issues.push({ severity: "error", path: `${path}.id`, message: `Duplicate id "${value.id}" within this list.` });
  } else {
    seenIds.add(value.id);
  }

  if (!isNonEmptyString(value.contentId)) {
    issues.push({ severity: "error", path: `${path}.contentId`, message: "contentId must be a non-empty string." });
  }

  if (typeof value.active !== "boolean") {
    issues.push({ severity: "error", path: `${path}.active`, message: "active must be a boolean." });
  }

  if (!isDateString(value.startDate)) {
    issues.push({ severity: "error", path: `${path}.startDate`, message: "startDate must be a valid date string." });
  }
  if (!isDateString(value.endDate)) {
    issues.push({ severity: "error", path: `${path}.endDate`, message: "endDate must be a valid date string." });
  }
  if (
    isDateString(value.startDate) &&
    isDateString(value.endDate) &&
    new Date(value.endDate).getTime() <= new Date(value.startDate).getTime()
  ) {
    issues.push({ severity: "error", path: `${path}.endDate`, message: "endDate must be after startDate." });
  }

  if (value.customJSON !== null && value.customJSON !== undefined && !isPlainObject(value.customJSON)) {
    issues.push({
      severity: "error",
      path: `${path}.customJSON`,
      message: "customJSON must be a JSON object or null.",
    });
  }
}

function validateList(value: unknown, listKey: LiveOpsListKey, issues: EventIssue[]): void {
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    issues.push({ severity: "error", path: listKey, message: `${listKey} must be an array.` });
    return;
  }

  const seenIds = new Set<string>();
  value.forEach((row, index) => validateScheduleRow(row, `${listKey}[${index}]`, seenIds, issues));
}

/** Normalize missing lists to empty arrays for reads/saves. */
export function normalizeEventsConfig(parsed: unknown): EventsConfig {
  if (!isPlainObject(parsed)) {
    return { lobbyBanners: [], eventBanners: [], events: [] };
  }
  return {
    lobbyBanners: Array.isArray(parsed.lobbyBanners) ? (parsed.lobbyBanners as LiveOpsScheduleRow[]) : [],
    eventBanners: Array.isArray(parsed.eventBanners) ? (parsed.eventBanners as LiveOpsScheduleRow[]) : [],
    events: Array.isArray(parsed.events) ? (parsed.events as LiveOpsScheduleRow[]) : [],
  };
}

/**
 * Validate parsed LiveOps schedule before it reaches the game. Pure so the
 * admin UI and Worker apply the same write-time rules.
 */
export function validateEventsConfig(parsed: unknown): EventIssue[] {
  const issues: EventIssue[] = [];
  if (!isPlainObject(parsed)) {
    issues.push({ severity: "error", path: "", message: "Root value must be a JSON object." });
    return issues;
  }

  addUnknownKeyIssues(parsed, ROOT_KEYS, "", issues);
  validateList(parsed.lobbyBanners, "lobbyBanners", issues);
  validateList(parsed.eventBanners, "eventBanners", issues);
  validateList(parsed.events, "events", issues);

  return issues;
}

export function emptyEventsConfig(): EventsConfig {
  return { lobbyBanners: [], eventBanners: [], events: [] };
}

export function newScheduleRow(): LiveOpsScheduleRow {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return {
    id: "",
    contentId: "",
    active: true,
    startDate: now.toISOString(),
    endDate: tomorrow.toISOString(),
    customJSON: null,
  };
}
